/**
 * +castux: WoW-style ground-targeted placement (pure state machine, no DOM).
 *
 * Pressing a bound ground spell (Earthquake) enters placement: a circle of the spell's real
 * radius follows the cursor (render/toon/placement.ts), valid = in range and on walkable floor.
 * Left-click or the same key again confirms at the cursor; right-click or Esc cancels; another
 * ability key cancels and casts that ability. With "Quick cast ground spells" on, the key casts
 * at the cursor at once (the old behaviour). Dodge also uses 'ground' targeting internally but
 * is never placed (it is on Shift, and is excluded here too in case a class binds it).
 *
 * The confirmed cast is an ordinary `cast` Command with its `point`, so prediction, the spell
 * queue and multiplayer (net/predict.ts, the room) see nothing new.
 */
import type { AbilityDef, DeepReadonly, Vec2 } from '@keystone/sim';
import { ABILITY } from '@keystone/content';
import type { UiBind } from '../input/bindings';

type GroundAbility = Pick<DeepReadonly<AbilityDef>, 'id' | 'targeting' | 'range' | 'effects'>;

/** Fallback circle (yd) when a ground spell has no point-anchored shape to read. */
export const DEFAULT_GROUND_RADIUS = 3;

/** Does pressing this ability's key enter placement? (Bound ground spells only; never Dodge.) */
export function placesOnGround(ab: Pick<AbilityDef, 'id' | 'targeting'> | undefined | null): boolean {
  return !!ab && ab.targeting === 'ground' && ab.id !== ABILITY.common.dodge;
}

/** Radius (yd) of the area the spell drops at its ground point: zone / area effects anchored at 'point'. */
export function groundRadius(ab: Pick<DeepReadonly<AbilityDef>, 'effects'> | undefined | null): number {
  let r = 0;
  for (const e of ab?.effects ?? []) {
    const ex = e as { type: string; anchor?: string; shape?: { type: string; radius?: number; outer?: number }; area?: { anchor: string; shape: { type: string; radius?: number; outer?: number } } };
    const shape = ex.type === 'zone' && ex.anchor === 'point' ? ex.shape : ex.area?.anchor === 'point' ? ex.area.shape : undefined;
    if (!shape) continue;
    const v = shape.type === 'circle' ? (shape.radius ?? 0) : shape.type === 'ring' ? (shape.outer ?? 0) : 0;
    if (v > r) r = v;
  }
  return r > 0 ? r : DEFAULT_GROUND_RADIUS;
}

export type PlacementCheck = 'ok' | 'no_point' | 'out_of_range' | 'not_walkable';

/** Same range rule as the sim's ground cast (center distance ≤ range + caster reach), plus a walkable floor. */
export function checkPlacement(from: Vec2, reach: number, point: Vec2 | null, range: number | undefined, walkable: (p: Vec2) => boolean): PlacementCheck {
  if (!point) return 'no_point';
  if (range && Math.hypot(point.x - from.x, point.y - from.y) > range + reach) return 'out_of_range';
  return walkable(point) ? 'ok' : 'not_walkable';
}

/** Error line for a placement that cannot be confirmed. */
export const PLACEMENT_ERRORS: Readonly<Record<Exclude<PlacementCheck, 'ok'>, string>> = {
  no_point: 'Invalid location',
  out_of_range: 'Out of range',
  not_walkable: 'Invalid location',
};

export interface Placing {
  bind: UiBind;
  /** Bound (base) id: what the confirmed command sends. */
  abilityId: string;
  radius: number;
  range: number;
}

/**
 * What a key press should do:
 *  - `pass`: not a placed spell (and nothing to cancel): cast as usual;
 *  - `cancel-pass`: another key while placing: placement was cancelled, cast that key as usual;
 *  - `quick`: quick-cast is on: cast at the cursor now (as usual, the cursor is the point);
 *  - `enter`: placement started (or switched to this spell);
 *  - `confirm`: the same key again: confirm at the cursor.
 */
export type PressOutcome = 'pass' | 'cancel-pass' | 'quick' | 'enter' | 'confirm';

export class GroundPlacement {
  active: Placing | null = null;

  /** `ability`: the form the key casts right now (a transform can change its targeting). `baseId`: the bound id. */
  press(bind: UiBind, baseId: string | null, ability: GroundAbility | undefined, quickCast: boolean): PressOutcome {
    const a = this.active;
    if (!baseId || !placesOnGround(ability)) {
      if (!a) return 'pass';
      this.active = null;
      return 'cancel-pass';
    }
    if (a && a.bind === bind && a.abilityId === baseId) return 'confirm';
    if (quickCast) {
      this.active = null;
      return a ? 'cancel-pass' : 'quick';
    }
    this.active = { bind, abilityId: baseId, radius: groundRadius(ability), range: ability!.range ?? 0 };
    return 'enter';
  }

  /** End placement after a successful confirm. */
  take(): Placing | null {
    const a = this.active;
    this.active = null;
    return a;
  }

  /** Right-click / Esc / death / pause. Returns true when something was cancelled. */
  cancel(): boolean {
    const had = !!this.active;
    this.active = null;
    return had;
  }
}
